/* eslint-disable no-console */
import React, { useState } from 'react';
import {
  Modal,
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { handleSendTech } from '../../hooks/sendBooking';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: { xs: 'calc(100vw - 32px)', sm: 420 },
  maxHeight: '90vh',
  overflowY: 'auto',
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: { xs: 2.5, sm: 4 },
};
const calculateMinStay = (startDate, endDate) => {
  const start = new Date(startDate);
  const end = new Date(endDate);
  const month = start.getMonth() + 1;
  const dayDifference = (end - start) / (1000 * 60 * 60 * 24);

  let minStay = 4; // Default min stay
  if (month === 6 || month === 8) {
    minStay = 3;
  } else if (month === 7) {
    minStay = 2;
  }

  return { dayDifference, minStay };
};

const getTodayFormatted = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

function ModalBooking({ open, onClose, apartment }) {
  const { t, i18n } = useTranslation();
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [adults, setAdults] = useState(2);
  const [childrenCount, setChildrenCount] = useState(0);
  const [errors, setErrors] = useState({});
  const [nameBooker, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [responseMessage, setResponseMessage] = useState('');
  const [submitSucceeded, setSubmitSucceeded] = useState(false);

  const validateEmail = (emailValue) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(String(emailValue).toLowerCase());
  };

  const validatePhone = (phoneValue) => {
    const re = /^\+?\d+$/;
    return re.test(phoneValue);
  };

  const validateForm = () => {
    const newErrors = {};
    const adultCount = Number(adults);
    const childCount = Number(childrenCount);
    if (!nameBooker) newErrors.nameBooker = t('booking.errors.nameRequired');
    if (!startDate) newErrors.startDate = t('booking.errors.startDateRequired');
    if (!endDate) newErrors.endDate = t('booking.errors.endDateRequired');

    if (startDate) {
      const today = getTodayFormatted();
      if (startDate < today) {
        newErrors.startDate = t('booking.errors.startDatePast');
      }
    }

    if (!email) {
      newErrors.email = t('booking.errors.emailRequired');
    } else if (!validateEmail(email)) {
      newErrors.email = t('booking.errors.emailInvalid');
    }
    if (phone && !validatePhone(phone)) {
      newErrors.phone = t('booking.errors.phoneInvalid');
    }
    if (!Number.isInteger(adultCount) || adultCount < 1) {
      newErrors.adults = t('booking.errors.adultsRequired');
    }
    if (!Number.isInteger(childCount) || childCount < 0) {
      newErrors.children = t('booking.errors.childrenInvalid');
    }
    // if (startDate && endDate) {
    //   const { dayDifference, minStay } = calculateMinStay(startDate, endDate);
    //   if (dayDifference < minStay) {
    //     newErrors.dateRange = `The minimum stay in this period is ${minStay} days`;
    //   }
    // }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleConfirm = async () => {
    if (validateForm()) {
      setLoading(true);
      const formData = {
        apartmentId: apartment.id,
        apartmentName: apartment.name,
        startDate,
        endDate,
        email,
        phone,
        adults: Number(adults),
        children: Number(childrenCount),
        nameBooker,
        locale: i18n.language,
      };
      try {
        const response = await handleSendTech(formData);
        if (response.status === 200) {
          setResponseMessage(t('booking.messages.success'));
          setSubmitSucceeded(true);
        } else {
          throw new Error('Request failed');
        }
      } catch (error) {
        console.error('Error occurred:', error);
        setResponseMessage(t('booking.messages.failure'));
        setSubmitSucceeded(false);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleDiscard = () => {
    onClose();
  };

  const renderCarBanner = () => (
    <Box
      sx={{
        mt: 3,
        p: 2,
        borderRadius: 1,
        background: 'linear-gradient(120deg, #0f1c3a, #1f2f4d)',
        color: 'common.white',
        textAlign: 'center',
      }}
    >
      <Box
        component="img"
        src="/carsnk/logo.png"
        alt="CarsNK"
        width={64}
        height={64}
        loading="lazy"
        decoding="async"
        sx={{
          width: 64,
          height: 64,
          borderRadius: 1.5,
          objectFit: 'cover',
          mb: 1.25,
        }}
      />
      <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
        Need a car in Nea Kallikratia?
      </Typography>
      <Typography
        variant="body2"
        sx={{ mt: 0.75, mb: 2, color: 'rgba(255,255,255,0.92)' }}
      >
        CarsNK.gr offers quick pickup, airport delivery on request, and local
        support.
      </Typography>
      <Button
        href="https://carsnk.gr/"
        target="_blank"
        rel="noopener noreferrer"
        variant="contained"
        color="secondary"
        fullWidth
        sx={{ color: 'common.white', fontWeight: 700, textTransform: 'none' }}
      >
        Book at CarsNK.gr
      </Button>
    </Box>
  );

  const renderForm = () => (
    <>
      <TextField
        label={t('booking.fields.name')}
        type="text"
        value={nameBooker}
        onChange={(e) => setName(e.target.value)}
        fullWidth
        margin="normal"
        InputLabelProps={{ shrink: true }}
        error={!!errors.nameBooker}
        helperText={errors.nameBooker}
      />
      <TextField
        label={t('booking.fields.startDate')}
        type="date"
        value={startDate}
        onChange={(e) => setStartDate(e.target.value)}
        fullWidth
        margin="normal"
        InputLabelProps={{ shrink: true }}
        inputProps={{ min: getTodayFormatted() }}
        error={!!errors.startDate}
        helperText={errors.startDate}
      />
      <TextField
        label={t('booking.fields.endDate')}
        type="date"
        value={endDate}
        onChange={(e) => setEndDate(e.target.value)}
        fullWidth
        margin="normal"
        InputLabelProps={{ shrink: true }}
        inputProps={{ min: startDate || getTodayFormatted() }}
        error={!!errors.endDate}
        helperText={errors.endDate}
      />
      {errors.dateRange && (
        <Typography color="secondary" variant="h6">
          {errors.dateRange}
        </Typography>
      )}
      <TextField
        label={t('booking.fields.email')}
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        fullWidth
        margin="normal"
        error={!!errors.email}
        helperText={errors.email}
      />
      <TextField
        label={t('booking.fields.phone')}
        type="tel"
        value={phone}
        onChange={(e) => setPhone(e.target.value)}
        fullWidth
        margin="normal"
        error={!!errors.phone}
        helperText={errors.phone}
      />
      <Box sx={{ display: 'flex', gap: 2 }}>
        <TextField
          label={t('booking.fields.adults')}
          type="number"
          value={adults}
          onChange={(e) => setAdults(e.target.value)}
          fullWidth
          margin="normal"
          inputProps={{ min: 1, step: 1 }}
          error={!!errors.adults}
          helperText={errors.adults}
        />
        <TextField
          label={t('booking.fields.children')}
          type="number"
          value={childrenCount}
          onChange={(e) => setChildrenCount(e.target.value)}
          fullWidth
          margin="normal"
          inputProps={{ min: 0, step: 1 }}
          error={!!errors.children}
          helperText={errors.children}
        />
      </Box>
      <Box mt={2} display="flex" justifyContent="space-between">
        <Button variant="outlined" color="primary" onClick={handleDiscard}>
          {t('booking.actions.discard')}
        </Button>
        <Button
          variant="contained"
          color="secondary"
          onClick={handleConfirm}
          disabled={loading}
        >
          {loading ? (
            <CircularProgress size={24} />
          ) : (
            t('booking.actions.confirm')
          )}
        </Button>
      </Box>
    </>
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="modal-title"
      aria-describedby="modal-description"
    >
      <Box sx={style}>
        <Typography id="modal-title" variant="h6" component="h2">
          {t('booking.title', { apartmentName: apartment.name })}
        </Typography>
        {responseMessage ? (
          <>
            <Typography variant="body1" sx={{ mt: 2 }}>
              {responseMessage}
            </Typography>
            {submitSucceeded && renderCarBanner()}
            <Button
              variant="outlined"
              color="primary"
              onClick={onClose}
              fullWidth
              sx={{ mt: 2 }}
            >
              {t('apModal.close')}
            </Button>
          </>
        ) : (
          renderForm()
        )}
      </Box>
    </Modal>
  );
}

export default ModalBooking;
